import Navbar from '../../home/components/Navbar';
import Footer from '../../home/components/Footer';
import RelatedServices from '../../../components/feature/RelatedServices';
import RelatedIndustries from '../../../components/feature/RelatedIndustries';

export default function HealthcarePPCPage() {
  const features = [
    'Google Ads Campaign Management',
    'Meta Ads (Facebook & Instagram)',
    'HIPAA-Compliant Ad Targeting',
    'Landing Page Optimization',
    'Conversion Tracking Setup',
    'A/B Testing & Optimization',
    'Remarketing Campaigns',
    'Negative Keyword Management',
    'Ad Copy Creation & Testing',
    'Budget Optimization',
    'Competitor Analysis',
    'Monthly Performance Reports'
  ];

  const process = [
    {
      step: '01',
      title: 'Strategy & Planning',
      description: 'Define target patients, services to promote, budget allocation, and campaign goals with clear ROI targets.'
    },
    {
      step: '02',
      title: 'Campaign Setup',
      description: 'Build HIPAA-compliant campaigns with proper targeting, ad groups, keywords, and conversion tracking.'
    },
    {
      step: '03',
      title: 'Ad Creation',
      description: 'Write compelling ad copy and design visuals that speak to patient pain points and drive clicks.'
    },
    {
      step: '04',
      title: 'Landing Page Optimization',
      description: 'Create or optimize landing pages designed specifically to convert ad traffic into patient appointments.'
    },
    {
      step: '05',
      title: 'Launch & Monitor',
      description: 'Launch campaigns and monitor performance daily, making real-time adjustments for optimal results.'
    },
    {
      step: '06',
      title: 'Optimize & Scale',
      description: 'Continuously test, refine, and scale winning campaigns while eliminating underperformers.'
    }
  ];

  const benefits = [
    {
      icon: 'ri-flashlight-line',
      title: 'Instant Patient Flow',
      description: 'Start generating qualified patient leads immediately, unlike SEO which takes months to build momentum.'
    },
    {
      icon: 'ri-focus-3-line',
      title: 'Precise Targeting',
      description: 'Reach patients based on location, demographics, interests, and search intent with laser precision.'
    },
    {
      icon: 'ri-line-chart-line',
      title: 'Measurable ROI',
      description: 'Track every dollar spent and every patient acquired with detailed conversion tracking and attribution.'
    },
    {
      icon: 'ri-shield-check-line',
      title: 'HIPAA-Compliant Campaigns',
      description: 'All targeting, tracking, and patient data handling follows strict HIPAA compliance guidelines.'
    },
    {
      icon: 'ri-funds-line',
      title: 'Budget Control',
      description: 'Set daily budgets, pause campaigns anytime, and scale up or down based on your practice capacity.'
    },
    {
      icon: 'ri-test-tube-line',
      title: 'Continuous Optimization',
      description: 'Ongoing A/B testing and refinement ensures your cost per patient acquisition decreases over time.'
    }
  ];

  const platforms = [
    {
      name: 'Google Ads',
      icon: 'ri-google-fill',
      description: 'Capture high-intent patients actively searching for your healthcare services right now.',
      features: ['Search Ads', 'Display Ads', 'Local Service Ads', 'YouTube Ads']
    },
    {
      name: 'Meta Ads',
      icon: 'ri-meta-fill',
      description: 'Build awareness and reach patients on Facebook and Instagram with targeted healthcare campaigns.',
      features: ['Facebook Ads', 'Instagram Ads', 'Messenger Ads', 'Audience Network']
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6 lg:px-12 bg-gradient-to-br from-[#F5F3FF] via-white to-[#FAF5FF] relative overflow-hidden">
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-[#7C3AED] font-bold mb-4">
                HEALTHCARE PPC ADVERTISING
              </div>
              <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 leading-tight mb-6">
                Generate Qualified Patient Leads on Demand
              </h1>
              <p className="text-lg text-gray-600 leading-relaxed mb-8">
                HIPAA-compliant Google Ads and Meta campaigns that fill your schedule with qualified patients. Pay only for results with full transparency and measurable ROI.
              </p>
              <div className="flex flex-wrap gap-4">
                <a
                  href="/book-strategy-call"
                  className="inline-flex items-center gap-2 bg-gradient-to-r from-[#7C3AED] to-[#A855F7] text-white px-8 py-4 rounded-lg hover:shadow-lg transition-all cursor-pointer whitespace-nowrap font-bold"
                >
                  <span>Get Your Free PPC Audit</span>
                  <i className="ri-arrow-right-line"></i>
                </a>
                <a
                  href="#process"
                  className="inline-flex items-center gap-2 bg-white text-[#7C3AED] border-2 border-[#7C3AED] px-8 py-4 rounded-lg hover:bg-[#7C3AED]/5 transition-all cursor-pointer whitespace-nowrap font-medium"
                >
                  <span>See Our Process</span>
                </a>
              </div>
            </div>
            <div className="relative">
              <div className="w-full h-96 rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="https://public.readdy.ai/ai/img_res/db8c945174c02b6182bcb1f68fd92fe4.jpg"
                  alt="Healthcare PPC dashboard"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Platforms Section */}
      <section className="py-24 px-6 lg:px-12 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="text-xs uppercase tracking-[0.2em] text-[#7C3AED] font-bold mb-4">
              ADVERTISING PLATFORMS
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 leading-tight mb-6">
              Reach Patients Where They Search &amp; Scroll
            </h2>
            <p className="text-base text-gray-600 max-w-3xl mx-auto">
              We manage HIPAA-compliant campaigns across the platforms that matter most for healthcare patient acquisition.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {platforms.map((platform, index) => (
              <div
                key={index}
                className="bg-[#F9FAFB] rounded-xl p-10 hover:shadow-xl transition-all duration-300 border border-gray-100"
              >
                <div className="w-16 h-16 bg-gradient-to-br from-[#7C3AED] to-[#A855F7] rounded-xl flex items-center justify-center mb-6">
                  <i className={`${platform.icon} text-3xl text-white`}></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  {platform.name}
                </h3>
                <p className="text-gray-600 leading-relaxed mb-6">
                  {platform.description}
                </p>
                <div className="grid grid-cols-2 gap-3">
                  {platform.features.map((feature, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <div className="w-4 h-4 bg-[#7C3AED]/10 rounded-full flex items-center justify-center">
                        <i className="ri-check-line text-[#7C3AED] text-xs"></i>
                      </div>
                      <span className="text-gray-700 text-sm font-medium">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-24 px-6 lg:px-12 bg-[#F9FAFB]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="text-xs uppercase tracking-[0.2em] text-[#7C3AED] font-bold mb-4">
              WHY HEALTHCARE PPC
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 leading-tight mb-6">
              Fast, Predictable Patient Acquisition
            </h2>
            <p className="text-base text-gray-600 max-w-3xl mx-auto">
              PPC advertising gives you complete control over your patient flow with immediate results and transparent ROI tracking.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <div
                key={index}
                className="bg-white rounded-xl p-8 hover:shadow-xl transition-all duration-300 border border-gray-100"
              >
                <div className="w-14 h-14 bg-gradient-to-br from-[#7C3AED] to-[#A855F7] rounded-lg flex items-center justify-center mb-6">
                  <i className={`${benefit.icon} text-2xl text-white`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  {benefit.title}
                </h3>
                <p className="text-gray-600 leading-relaxed text-sm">
                  {benefit.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 px-6 lg:px-12 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-[#7C3AED] font-bold mb-4">
                COMPREHENSIVE PPC MANAGEMENT
              </div>
              <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 leading-tight mb-6">
                Full-Service Campaign Management
              </h2>
              <p className="text-base text-gray-600 leading-relaxed mb-8">
                From strategy to execution to optimization, we handle every aspect of your healthcare PPC campaigns so you can focus on treating patients.
              </p>
              <div className="grid sm:grid-cols-2 gap-4">
                {features.map((feature, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <div className="w-5 h-5 bg-[#7C3AED]/10 rounded-full flex items-center justify-center mt-0.5 flex-shrink-0">
                      <i className="ri-check-line text-[#7C3AED] text-sm"></i>
                    </div>
                    <span className="text-gray-700 text-sm font-medium">{feature}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <div className="w-full h-[500px] rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="https://readdy.ai/api/search-image?query=PPC%20advertising%20campaign%20management%20showing%20ad%20performance%20metrics%20conversion%20rates%20cost%20per%20acquisition%20and%20patient%20lead%20tracking%20healthcare%20marketing%20analytics%20dashboard%20with%20purple%20accents%20professional%20interface%20clean%20modern%20design&width=600&height=700&seq=ppcfeatures1&orientation=portrait"
                  alt="PPC management features"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section id="process" className="py-24 px-6 lg:px-12 bg-[#F9FAFB]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="text-xs uppercase tracking-[0.2em] text-[#7C3AED] font-bold mb-4">
              OUR PPC PROCESS
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 leading-tight mb-6">
              From Strategy to Profitable Campaigns
            </h2>
            <p className="text-base text-gray-600 max-w-3xl mx-auto">
              Our proven process ensures your campaigns launch strong and improve continuously for maximum patient ROI.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {process.map((item, index) => (
              <div
                key={index}
                className="bg-white rounded-xl p-8 hover:shadow-lg transition-all duration-300 border border-gray-100"
              >
                <div className="w-16 h-16 bg-gradient-to-br from-[#7C3AED] to-[#A855F7] rounded-xl flex items-center justify-center mb-6">
                  <span className="text-2xl font-bold text-white">{item.step}</span>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  {item.title}
                </h3>
                <p className="text-gray-600 leading-relaxed text-sm">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6 lg:px-12 bg-gradient-to-br from-[#0D9488] to-[#14B8A6] relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-transparent to-black/10"></div>
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <h2 className="text-4xl lg:text-5xl font-bold text-white leading-tight mb-6">
            Ready to Fill Your Schedule with Qualified Patients?
          </h2>
          <p className="text-lg text-white/90 mb-8 leading-relaxed">
            Get a free PPC audit and custom campaign strategy showing exactly how we'll generate patient leads for your practice.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <a
              href="/book-strategy-call"
              className="inline-flex items-center gap-2 bg-white text-[#0D9488] px-8 py-4 rounded-lg hover:bg-gray-50 transition-all cursor-pointer whitespace-nowrap font-bold"
            >
              <span>Get Your Free PPC Audit</span>
              <i className="ri-arrow-right-line"></i>
            </a>
            <a
              href="/#contact"
              className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm text-white border-2 border-white/30 px-8 py-4 rounded-lg hover:bg-white/20 transition-all cursor-pointer whitespace-nowrap font-medium"
            >
              <span>See Campaign Results</span>
            </a>
          </div>
        </div>
      </section>

      <RelatedServices currentSlug="healthcare-ppc" />
      <RelatedIndustries />

      <Footer />
    </div>
  );
}
